import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from bottom to trigger load (in pixels)
}

export const useInfiniteScroll = ({
  hasMore,
  loading,
  onLoadMore,
  threshold = 100,
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(hasMore);
  const loadingRef_state = useRef(loading);

  // Update refs when props change
  useEffect(() => {
    hasMoreRef.current = hasMore;
    loadingRef_state.current = loading;
  }, [hasMore, loading]);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasMoreRef.current && !loadingRef_state.current) {
        onLoadMore();
      }
    },
    [onLoadMore]
  );

  useEffect(() => {
    const element = loadingRef.current;
    if (!element) return;

    // Only create observer once
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(handleObserver, {
        rootMargin: `${threshold}px`,
      });
      observerRef.current.observe(element);
    }

    // Disconnect observer when there are no more places to load
    if (!hasMore && observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleObserver, threshold, hasMore]);

  return loadingRef;
}; 